import { Matrix4, Object3D, Quaternion, Vector3 } from "three";

import { UUID } from "../../utils/typeDefinitions";
import SceneObjectData from "../sceneObjectData/SceneObjectData";
import EditHandleData from "../sceneObjectData/EditHandleData";
import ISceneGeometry from "./ISceneGeometry";
import ISceneObject from "./ISceneObject";
import { SceneManager } from "../SceneManager";
import { createSceneGeometry } from "./SceneGeometryFactory";
import { findProductFromSceneByGlobalId } from "../../utils/geometryUtils";
import PropertyList from "../properties/PropertyList";
import { PropertyId } from "../properties/PropertyId";

type SceneObjectFactoryFn = (dto: SceneObjectData) => ISceneObject;

// SceneObjectFactory imports every concrete SceneObject subclass, which in turn extends this
// class - importing it here directly would create a circular dependency. SceneObjectFactory
// registers itself via registerSceneObjectFactory() as soon as its module loads instead.
let sceneObjectFactory: SceneObjectFactoryFn | null = null;

export function registerSceneObjectFactory(factory: SceneObjectFactoryFn): void {
    sceneObjectFactory = factory;
}

export default class SceneObject<T extends SceneObjectData> implements ISceneObject {
    
    geometry: ISceneGeometry;
    data: T;
    parentObject: Object3D | null = null;
    
    constructor(data: T) {
        this.data = data;
        this.geometry = createSceneGeometry(data.type);
        this.geometry.update(data, false);
    } 

    updateGeometry(forceUpdate: boolean): boolean {
        return this.geometry.update(this.data, forceUpdate);
    }

    addToScene(): void {
        // If objects has parent, convert geometry to local coordinate system of the parent
        const parent = this.getParentObject();
        if (parent) {
            parent.add(this.geometry.group);
            this.updateGeometry(true);
            return; // Adding group to parent is enough, no need to add to scene again
        }
        else if (this.data.parentId) {
            console.warn(`SceneObject.addToScene: Parent with id ${this.data.parentId} not found. Adding to scene without parent.`);
        }
        this.geometry.addToScene();
    }

    getParentObject(): Object3D | null {

        if (this.parentObject) {
            return this.parentObject;
        }

        const scene = SceneManager.scene;
        if (this.data.parentId) {
            const parent = findProductFromSceneByGlobalId(scene!, this.data.parentId);
            if (parent) {
                this.parentObject = parent;
                return parent;
            } else {
                console.warn(`SceneObject.getParentObject: Parent with id ${this.data.parentId} not found.`);
            }
        }
        return null;
    }

    removeFromScene(): void {
        this.geometry.removeFromScene();
    }

    clone(detachFromParent: boolean): ISceneObject {
        
        const dataClone = this.data.clone();
        const objectClone = sceneObjectFactory!(dataClone);

        if (detachFromParent) {
            if (!this.data.parentId)
                console.error("SceneObject.clone: Trying to detach from parent, but object has no parentId.");

            const wm = this.getWorldMatrix();
            objectClone.data.parentId = undefined;
            objectClone.geometry.group.applyMatrix4(wm);

            // Some geometries (e.g. ChecklistItemSubGeometry2d) compute their local rotation by
            // canceling out their parent's current world rotation. That computation ran once already
            // during construction above, before this world-matrix bake existed, so it canceled nothing.
            // Re-running the update now lets those geometries recompute against the now-baked transform.
            objectClone.geometry.group.updateWorldMatrix(true, false);
            objectClone.geometry.update(objectClone.data, true);
        }

        return objectClone;
    }

    canClone(): boolean {
        return true;
    }

    hasSelectionContextToolbar(): boolean {
        return true;
    }

    // Sets parent object of this scene object and converts it into parent's own local coordinate system
    setParent(parent: Object3D | null): void {
        
        // If we found a global id, we make placement of this object relative to the parent object
        if (this.data.parentId) {

            parent!.add(this.geometry.group);

            const parentInverseMatrix = new Matrix4().copy(parent!.matrixWorld).invert();
            this.data.applyMatrix4(parentInverseMatrix);
            this.data.parentId = this.data.parentId ?? parent!.uuid as UUID;
        }
    }

    getWorldMatrix(): Matrix4 {
        this.geometry.group.updateWorldMatrix(true, false);
        return this.geometry.group.matrixWorld;
    }

    onAnnotationDirectionChange(rotationOffset: number) {
        this.geometry.onAnnotationDirectionChange(rotationOffset);
    }

    // Subclasses that offer handles use localViewDirection (this object's local space) to drop
    // the ones that would be useless from the current viewpoint. See ISceneObject.
    getEditHandles(_localViewDirection: Vector3): EditHandleData[] {
        return [];
    }

    // Root of the chain-of-responsibility: no common properties today, but subclasses call
    // super.getProperties()/super.setProperties() first before adding their own, and are
    // responsible for their own geometry refresh after mutating fields in setProperties.
    getProperties(_props: PropertyList, _filterId?: PropertyId): void {
    }

    setProperties(_props: PropertyList): void {
    }

    dispose() {
        this.geometry.dispose();
        this.parentObject = null;
    }
}
